import { useRef, useState, useEffect } from 'react';
import {
  motion,
  AnimatePresence,
  useScroll,
  useTransform,
  useMotionValue,
  useSpring,
  useMotionTemplate,
  animate,
} from 'framer-motion';
import { ArrowUpRight, Download, Sparkles } from 'lucide-react';
import SignalGrid from '../components/SignalGrid';

const EASE = [0.16, 1, 0.3, 1];

const credentials = [
  { label: 'Education', value: 'MSc Data Science', sub: 'Loughborough · Distinction Expected', accent: '#9B3FFE' },
  { label: 'Consulting', value: '1,000+ Engagements', sub: 'AlphaSights · Tegus · Guidepoint · NewtonX', accent: '#00E5FF' },
  { label: 'Analytics', value: '5★ Rated', sub: 'Upwork · Data & Strategy', accent: '#10B981' },
  { label: 'Recognition', value: 'AWS AI Scholar', sub: 'Udacity × AWS · 2026', accent: '#F59E0B' },
];

const NETWORKS = ['AlphaSights', 'Tegus', 'Guidepoint', 'NewtonX'];

const TERMINAL_LINES = [
  'Synthesising expert-network insight',
  'Training churn model · XGBoost + SHAP',
  'Evaluating LLM outputs · RLHF',
  'Automating pipelines · n8n + APIs',
  'Briefing strategy teams on AI',
];

const METRICS = [
  { to: 1000, suffix: '+', label: 'Engagements' },
  { to: 6, suffix: '', label: 'Languages' },
  { to: 5, suffix: '★', label: 'Upwork' },
];

/* ─── Count-up number ─────────────────────────────────────────────────── */
const CountUp = ({ to, suffix, delay = 0 }) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    const controls = animate(0, to, {
      duration: 2.2,
      delay,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setVal(Math.round(v)),
    });
    return () => controls.stop();
  }, [to, delay]);
  return (
    <span className="stat-number">
      {val.toLocaleString('en-GB')}
      {suffix}
    </span>
  );
};

/* ─── Cycling terminal line ───────────────────────────────────────────── */
const TerminalLine = () => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI((n) => (n + 1) % TERMINAL_LINES.length), 2800);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="flex items-center gap-2.5 font-mono text-[11.5px] text-white/70 h-5 overflow-hidden">
      <span className="text-accent-cyan">❯</span>
      <AnimatePresence mode="wait">
        <motion.span
          key={i}
          initial={{ y: 14, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -14, opacity: 0 }}
          transition={{ duration: 0.35, ease: EASE }}
          className="truncate"
        >
          {TERMINAL_LINES[i]}
        </motion.span>
      </AnimatePresence>
      <span className="hero-caret" />
    </div>
  );
};

/* ─── Orbital AI core ─────────────────────────────────────────────────── */
const OrbitalCore = () => (
  <div className="relative mx-auto aspect-square w-full max-w-[260px]" aria-hidden="true">
    {/* Ambient glow */}
    <div className="absolute inset-[18%] rounded-full hero-core-glow" />

    {/* Rings */}
    <div className="absolute inset-0 rounded-full border border-white/[0.06]" />
    <div className="absolute inset-0 hero-spin-slow">
      <span className="hero-orbit-dot" style={{ top: '-4px', left: 'calc(50% - 4px)', background: '#9B3FFE', boxShadow: '0 0 14px 3px rgba(155,63,254,0.7)' }} />
    </div>
    <div className="absolute inset-[14%] rounded-full border border-dashed border-white/[0.09]" />
    <div className="absolute inset-[14%] hero-spin-reverse">
      <span className="hero-orbit-dot" style={{ bottom: '-4px', left: 'calc(50% - 4px)', background: '#00E5FF', boxShadow: '0 0 14px 3px rgba(0,229,255,0.7)' }} />
      <span className="hero-orbit-dot" style={{ top: 'calc(50% - 3px)', left: '-3px', width: 6, height: 6, background: '#F59E0B', boxShadow: '0 0 10px 2px rgba(245,158,11,0.6)' }} />
    </div>
    <div className="absolute inset-[28%] rounded-full border border-white/[0.08]" />
    <div className="absolute inset-[28%] hero-spin-fast">
      <span className="hero-orbit-dot" style={{ top: 'calc(50% - 3px)', right: '-3px', width: 6, height: 6, background: '#10B981', boxShadow: '0 0 10px 2px rgba(16,185,129,0.7)' }} />
    </div>

    {/* Conic sweep */}
    <div className="absolute inset-[36%] rounded-full hero-conic hero-spin-fast" />

    {/* Core sphere */}
    <div className="absolute inset-[39%] rounded-full hero-sphere flex items-center justify-center">
      <span className="font-display text-[15px] font-semibold tracking-[0.04em] text-white/95">RP</span>
    </div>
  </div>
);

/* ─── 3D tilt wrapper ─────────────────────────────────────────────────── */
const TiltCard = ({ children, className, style, max = 6, ...motionProps }) => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const cfg = { stiffness: 220, damping: 24, mass: 0.5 };
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [max, -max]), cfg);
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-max, max]), cfg);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => { mx.set(0); my.set(0); };

  return (
    <motion.div
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ rotateX, rotateY, transformStyle: 'preserve-3d', ...style }}
      className={className}
      {...motionProps}
    >
      {children}
    </motion.div>
  );
};

/* ─── Insight Engine console (replaces portrait) ──────────────────────── */
const InsightConsole = () => (
  <div className="relative" style={{ perspective: '1200px' }}>
    {/* Back glow */}
    <div className="absolute -inset-10 rounded-[48px] hero-console-glow pointer-events-none" />

    <TiltCard
      max={5}
      initial={{ opacity: 0, y: 40, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 1.1, delay: 0.45, ease: EASE }}
      className="relative rounded-[28px] hero-gradient-border"
    >
      <div className="relative overflow-hidden rounded-[27px] hero-console-surface">
        {/* Top sheen */}
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent" />

        {/* Window chrome */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
          <div className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-white/[0.12]" />
            <span className="h-2.5 w-2.5 rounded-full bg-white/[0.12]" />
            <span className="h-2.5 w-2.5 rounded-full bg-white/[0.12]" />
          </div>
          <span className="font-mono text-[10.5px] tracking-[0.08em] text-white/40">
            rayhan.ai / insight-engine
          </span>
          <span className="flex items-center gap-1.5 rounded-full border border-emerald-400/20 bg-emerald-400/[0.08] px-2.5 py-1">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-70" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
            </span>
            <span className="text-[9.5px] font-medium uppercase tracking-[0.18em] text-emerald-300">Live</span>
          </span>
        </div>

        {/* Core */}
        <div className="relative px-6 pt-8 pb-6">
          <div className="absolute inset-0 hero-console-grid pointer-events-none" />
          <OrbitalCore />
        </div>

        {/* Pipeline labels */}
        <div className="relative grid grid-cols-3 px-5 pb-5 text-center">
          {['Signal', 'System', 'Decision'].map((s, i) => (
            <div key={s} className="relative">
              <p className="text-[9.5px] uppercase tracking-[0.26em] text-white/40">0{i + 1}</p>
              <p className="mt-1 font-display text-[13px] font-medium text-white/85">{s}</p>
              {i < 2 && (
                <span className="absolute right-[-10%] top-1/2 h-px w-[20%] bg-gradient-to-r from-white/25 to-transparent" />
              )}
            </div>
          ))}
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-3 border-t border-white/[0.06]">
          {METRICS.map((m, i) => (
            <div
              key={m.label}
              className={`px-4 py-4 text-center ${i < 2 ? 'border-r border-white/[0.06]' : ''}`}
            >
              <p className="font-display text-[1.45rem] font-medium leading-none text-white">
                <CountUp to={m.to} suffix={m.suffix} delay={0.9 + i * 0.15} />
              </p>
              <p className="mt-1.5 text-[9.5px] uppercase tracking-[0.22em] text-white/40">{m.label}</p>
            </div>
          ))}
        </div>

        {/* Terminal */}
        <div className="border-t border-white/[0.06] bg-black/30 px-5 py-3.5">
          <TerminalLine />
        </div>
      </div>
    </TiltCard>

    {/* Floating chips */}
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.8, delay: 1.2, ease: EASE }}
      className="absolute -left-10 top-[22%] hidden xl:block"
    >
      <div className="hero-chip animate-float-slow">
        <span className="h-1.5 w-1.5 rounded-full bg-[#9B3FFE]" />
        Python · SQL · ML
      </div>
    </motion.div>
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.8, delay: 1.35, ease: EASE }}
      className="absolute -right-8 top-[48%] hidden xl:block"
    >
      <div className="hero-chip animate-float">
        <Sparkles className="h-3 w-3 text-amber-300" />
        AWS AI Scholar
      </div>
    </motion.div>
  </div>
);

/* ─── Hero ────────────────────────────────────────────────────────────── */
const Hero = ({ onBookCall }) => {
  const containerRef = useRef(null);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', check, { passive: true });
    return () => window.removeEventListener('resize', check);
  }, []);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start'],
  });
  const contentOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  // Cursor spotlight
  const spotX = useMotionValue(-1000);
  const spotY = useMotionValue(-1000);
  const spotlight = useMotionTemplate`radial-gradient(520px circle at ${spotX}px ${spotY}px, rgba(124,58,237,0.13), transparent 70%)`;
  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    spotX.set(e.clientX - r.left);
    spotY.set(e.clientY - r.top);
  };

  return (
    <section
      ref={containerRef}
      onMouseMove={isMobile ? undefined : onMouseMove}
      className="hero-min-h relative flex items-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* ── Backgrounds ─────────────────────────────────────── */}
      <div className="absolute inset-0 bg-bg-primary" />
      <div className="absolute inset-0 hero-aurora" aria-hidden="true">
        <div className="hero-aurora-blob hero-aurora-a" />
        <div className="hero-aurora-blob hero-aurora-b" />
        <div className="hero-aurora-blob hero-aurora-c" />
      </div>
      <div className="absolute inset-0 grid-pattern hero-grid-mask" />
      <SignalGrid />
      {!isMobile && (
        <motion.div className="absolute inset-0 pointer-events-none z-[2]" style={{ background: spotlight }} />
      )}
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-bg-primary z-[3] pointer-events-none" />

      {/* ── Main content ────────────────────────────────────── */}
      <motion.div
        className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 pt-28 sm:pt-32 pb-16 sm:pb-20 w-full"
        style={{
          opacity: isMobile ? undefined : contentOpacity,
          y: isMobile ? undefined : contentY,
        }}
      >
        <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-14 xl:gap-20 items-center">

          {/* ── Left column ───────────────────────────────── */}
          <div>
            {/* Status chip */}
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1, ease: EASE }}
              className="hero-status-chip mb-10"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
              </span>
              <span className="text-[10.5px] tracking-[0.2em] uppercase text-white/75 font-medium">
                Available June 2026<span className="hidden sm:inline"> · Analyst &amp; Strategy Roles</span>
              </span>
            </motion.div>

            {/* Name */}
            <h1
              className="font-display font-medium leading-[0.88] tracking-[-0.035em] mb-8"
              style={{ fontSize: 'clamp(60px, 10vw, 136px)' }}
              aria-label="Rayhan Patel"
            >
              <span className="block overflow-hidden pb-[0.04em]">
                <motion.span
                  className="block text-white"
                  initial={{ y: '110%' }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.2, ease: EASE }}
                >
                  Rayhan
                </motion.span>
              </span>
              <span className="block overflow-hidden pb-[0.08em]">
                <motion.span
                  className="block hero-name-gradient"
                  initial={{ y: '110%' }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.32, ease: EASE }}
                >
                  Patel.
                </motion.span>
              </span>
            </h1>

            {/* Tagline */}
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.55, ease: EASE }}
              className="font-display text-[1.45rem] md:text-[1.9rem] leading-[1.2] tracking-[-0.01em] text-white/90 max-w-[620px] mb-6"
            >
              I turn messy data into<br className="hidden md:block" />{' '}
              <span className="gradient-text-brand">decisions that move markets.</span>
            </motion.p>

            {/* Bio */}
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.68, ease: EASE }}
              className="text-[1rem] leading-[1.75] text-text-secondary max-w-[540px] mb-10"
            >
              MSc Data Science (Loughborough) · BSc Economics (Westminster).{' '}
              <span className="text-white font-medium">1,000+ expert network engagements</span>{' '}
              advising institutional investors and strategy teams on AI and data.
            </motion.p>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.8, ease: EASE }}
              className="grid grid-cols-1 min-[420px]:flex min-[420px]:flex-wrap gap-3 mb-12"
            >
              <motion.button
                onClick={onBookCall}
                className="hero-btn-primary group justify-center"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                data-testid="hero-book-call-btn"
              >
                <span className="relative z-10 flex items-center gap-2">
                  Book a Call
                  <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </motion.button>

              <motion.a
                href="/Rayhan_Patel_CV.pdf"
                download="Rayhan_Patel_CV.pdf"
                className="hero-btn-ghost group justify-center"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                data-testid="hero-download-cv-btn"
              >
                Download CV
                <Download className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-0.5" />
              </motion.a>
            </motion.div>

            {/* Trusted by */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1 }}
              className="flex flex-wrap items-center gap-x-7 gap-y-3"
            >
              <span className="text-[9.5px] uppercase tracking-[0.3em] text-white/35 font-medium">
                Trusted via
              </span>
              {NETWORKS.map((name) => (
                <span
                  key={name}
                  className="font-display text-[15px] font-medium tracking-[-0.01em] text-white/45 hover:text-white transition-colors duration-300"
                >
                  {name}
                </span>
              ))}
            </motion.div>
          </div>

          {/* ── Right column — Insight console ───────────── */}
          <div className="w-full max-w-[440px] mx-auto lg:mr-0">
            <InsightConsole />
          </div>
        </div>

        {/* ── Credential strip ──────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
          className="mt-16 lg:mt-20 grid grid-cols-2 lg:grid-cols-4 rounded-2xl border border-white/[0.07] bg-white/[0.02] backdrop-blur-md overflow-hidden"
        >
          {credentials.map((cred, i) => (
            <div
              key={cred.label}
              className={`group relative p-5 md:p-6 transition-colors duration-500 hover:bg-white/[0.025]
                ${i % 2 === 0 ? 'border-r' : ''} ${i < 2 ? 'border-b lg:border-b-0' : ''} ${i === 1 ? 'lg:border-r' : ''}
                border-white/[0.07]`}
            >
              <span
                className="absolute inset-x-0 top-0 h-px opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{ background: `linear-gradient(90deg, transparent, ${cred.accent}, transparent)` }}
              />
              <div className="flex items-center gap-2 mb-3">
                <span
                  className="h-1.5 w-1.5 rounded-full"
                  style={{ backgroundColor: cred.accent, boxShadow: `0 0 10px 2px ${cred.accent}66` }}
                />
                <p className="text-[9.5px] uppercase tracking-[0.26em] text-text-secondary font-medium">
                  {cred.label}
                </p>
              </div>
              <p className="font-display text-[1rem] md:text-[1.1rem] font-medium leading-tight mb-1.5 text-white">
                {cred.value}
              </p>
              <p className="text-[11px] text-text-secondary leading-snug">{cred.sub}</p>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
